<template>
  <section class="personal-center-panel">
    <h2>缴费记录</h2>

    <div class="payment-tabs" role="tablist" aria-label="缴费类型">
      <button
        type="button"
        role="tab"
        class="payment-tabs__btn"
        :class="{ 'is-active': activeTab === 'membership' }"
        :aria-selected="activeTab === 'membership' ? 'true' : 'false'"
        @click="activeTab = 'membership'"
      >
        会员费
        <em v-if="membershipTotal">{{ membershipTotal }}</em>
      </button>
      <button
        type="button"
        role="tab"
        class="payment-tabs__btn"
        :class="{ 'is-active': activeTab === 'conference' }"
        :aria-selected="activeTab === 'conference' ? 'true' : 'false'"
        @click="activeTab = 'conference'"
      >
        会议费
        <em v-if="conferenceTotal">{{ conferenceTotal }}</em>
      </button>
    </div>

    <div v-show="activeTab === 'membership'" role="tabpanel">
      <div v-if="pagedMembershipEntries.length" class="payment-history-list">
        <article
          v-for="item in pagedMembershipEntries"
          :key="item.key"
          class="payment-history-item"
        >
          <div class="payment-history-item__main">
            <strong>{{ item.title }}</strong>
            <span class="payment-history-item__meta">
              {{ item.associationName }}
              <template v-if="item.paidAtLabel"> · {{ item.paidAtLabel }}</template>
              <template v-if="item.validityLabel"> · {{ item.validityLabel }}</template>
              <template v-else-if="item.validEndDateLabel"> · 有效期至 {{ item.validEndDateLabel }}</template>
            </span>
            <span v-if="item.voucherUrl || item.invoiceUrl" class="payment-history-item__links">
              <a
                v-if="item.voucherUrl"
                href="javascript:;"
                @click.prevent="front.openSecureFile(item.voucherUrl)"
              >查看凭证</a>
              <a
                v-if="item.invoiceUrl"
                href="javascript:;"
                @click.prevent="front.openSecureFile(item.invoiceUrl)"
              >查看发票</a>
            </span>
          </div>
          <div class="payment-history-item__side">
            <strong class="payment-history-item__amount">
              {{ item.currencySymbol }}{{ formatAmount(item.amount) }}
            </strong>
            <em>{{ item.statusLabel }}</em>
          </div>
        </article>
      </div>
      <p v-else class="payment-section__empty">暂无会员费记录</p>
      <FrontPagination
        v-if="membershipTotal > pageSize"
        :page-num="membershipPageNum"
        :page-size="pageSize"
        :total="membershipTotal"
        @change="onMembershipPageChange"
      />
    </div>

    <div v-show="activeTab === 'conference'" role="tabpanel">
      <div v-if="pagedConferenceEntries.length" class="payment-history-list">
        <article
          v-for="item in pagedConferenceEntries"
          :key="item.key"
          class="payment-history-item"
        >
          <div class="payment-history-item__main">
            <strong>{{ item.title }}</strong>
            <span class="payment-history-item__meta">
              {{ item.associationName }}
              <template v-if="item.paidAtLabel"> · {{ item.paidAtLabel }}</template>
            </span>
            <span v-if="item.voucherUrl || item.invoiceUrl" class="payment-history-item__links">
              <a
                v-if="item.voucherUrl"
                href="javascript:;"
                @click.prevent="front.openSecureFile(item.voucherUrl)"
              >查看凭证</a>
              <a
                v-if="item.invoiceUrl"
                href="javascript:;"
                @click.prevent="front.openSecureFile(item.invoiceUrl)"
              >查看发票</a>
            </span>
          </div>
          <div class="payment-history-item__side">
            <strong class="payment-history-item__amount">
              {{ item.currencySymbol }}{{ formatAmount(item.amount) }}
            </strong>
            <em>{{ item.statusLabel }}</em>
          </div>
        </article>
      </div>
      <p v-else class="payment-section__empty">暂无会议费记录</p>
      <FrontPagination
        v-if="conferenceTotal > pageSize"
        :page-num="conferencePageNum"
        :page-size="pageSize"
        :total="conferenceTotal"
        @change="onConferencePageChange"
      />
    </div>
  </section>
</template>

<script>
import FrontPagination from '../../common/FrontPagination.vue'

export default {
  name: 'PersonalPaymentsPanel',
  components: { FrontPagination },
  inject: ['front'],
  data() {
    return {
      activeTab: 'membership',
      membershipPageNum: 1,
      conferencePageNum: 1,
      pageSize: 5
    }
  },
  computed: {
    allEntries() {
      return this.front.paymentHistoryEntries || []
    },
    membershipEntries() {
      return this.allEntries.filter(item => item.kind === 'membership')
    },
    conferenceEntries() {
      return this.allEntries.filter(item => item.kind === 'conference')
    },
    membershipTotal() {
      return this.membershipEntries.length
    },
    conferenceTotal() {
      return this.conferenceEntries.length
    },
    pagedMembershipEntries() {
      const start = (this.membershipPageNum - 1) * this.pageSize
      return this.membershipEntries.slice(start, start + this.pageSize)
    },
    pagedConferenceEntries() {
      const start = (this.conferencePageNum - 1) * this.pageSize
      return this.conferenceEntries.slice(start, start + this.pageSize)
    }
  },
  watch: {
    membershipTotal() {
      this.clampPage('membershipPageNum', this.membershipTotal)
    },
    conferenceTotal() {
      this.clampPage('conferencePageNum', this.conferenceTotal)
    }
  },
  methods: {
    formatAmount(value) {
      const num = Number(value)
      if (!Number.isFinite(num)) return '0'
      return Number.isInteger(num) ? String(num) : num.toFixed(2)
    },
    clampPage(field, total) {
      const maxPage = Math.max(1, Math.ceil(total / this.pageSize) || 1)
      if (this[field] > maxPage) {
        this[field] = maxPage
      }
    },
    onMembershipPageChange(page) {
      this.membershipPageNum = page
    },
    onConferencePageChange(page) {
      this.conferencePageNum = page
    }
  }
}
</script>

<style scoped>
.payment-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  margin: 0 0 16px;
  border-bottom: 1px solid rgba(0, 43, 73, 0.12);
}

.payment-tabs__btn {
  position: relative;
  margin: 0;
  padding: 10px 18px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #5f6670;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.payment-tabs__btn em {
  display: inline-block;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(0, 43, 73, 0.08);
  color: #5f6670;
  font-style: normal;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
}

.payment-tabs__btn:hover {
  color: #002b49;
}

.payment-tabs__btn.is-active {
  color: #c41e3a;
  border-bottom-color: #c41e3a;
}

.payment-tabs__btn.is-active em {
  background: rgba(196, 30, 58, 0.12);
  color: #c41e3a;
}

.payment-section__empty {
  margin: 0;
  color: #5f6670;
  font-size: 13px;
}

.payment-history-list {
  display: grid;
  gap: 10px;
  margin-bottom: 8px;
}

.payment-history-item {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px 16px;
  margin: 0;
  padding: 12px 14px;
  border: 1px solid rgba(31, 79, 122, 0.1);
  border-radius: 8px;
  background: #f8fafc;
  font-size: 13px;
  line-height: 1.55;
  color: #3d4a57;
}

.payment-history-item__main {
  display: grid;
  gap: 4px;
  min-width: 0;
  flex: 1 1 220px;
}

.payment-history-item__main strong {
  color: #002b49;
  font-size: 14px;
}

.payment-history-item__meta {
  color: #5f6670;
  font-size: 12px;
}

.payment-history-item__links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 12px;
}

.payment-history-item__links a {
  color: #1f4f7a;
  text-decoration: none;
}

.payment-history-item__links a:hover {
  text-decoration: underline;
}

.payment-history-item__side {
  display: grid;
  gap: 4px;
  justify-items: end;
  text-align: right;
  flex: 0 0 auto;
}

.payment-history-item__amount {
  color: #002b49;
  font-size: 15px;
}

.payment-history-item__side em {
  font-style: normal;
  color: #5f6670;
  font-size: 12px;
  font-weight: 600;
}
</style>
